import { render, useState, useRef, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const FrontendForm = ({ attributes }) => {
	const {
		showAddress = false,
		makeAddressRequired = false,
		showCountry = true,
		phoneIsRequired = false,
		showNotes = false,
		countryOptions = "",
		firstNameLabel = 'First Name',
		lastNameLabel = 'Last Name',
		emailLabel = 'Email',
		phoneLabel = 'Phone',
		address1Label = 'Address 1',
		address2Label = 'Address 2',
		cityLabel = 'City',
		stateLabel = 'State',
		zipLabel = 'Zip',
		countryLabel = 'Country',
		notesLabel = 'Comments or Questions',
		submitButtonLabel = 'Submit',
		successMessage = '<h3>Thank You</h3><p>You have been successfully added.</p>',
		errorMessage = '<h3>Something went wrong.</h3><p>Please try again.</p>',
		customFields = [],
		hiddenFields = [],
		tags = '',
	} = attributes || {};

	// ==========================================
	// HELPER FUNCTIONS
	// ==========================================
	const parseOptionsString = (optionsString) => {
		if (!optionsString) return [];
		const lines = optionsString.split('\n');
		return lines.map((line) => {
			const cleanLine = line.trim();
			if (!cleanLine) return null;
			const isDefault = cleanLine.startsWith('*');
			const textToParse = isDefault ? cleanLine.substring(1).trim() : cleanLine;
			const parts = textToParse.split(':');
			const label = parts[0].trim();
			const value = parts.length > 1 ? parts[1].trim() : label;
			return { label, value, isDefault };
		}).filter(Boolean);
	};

	const formatPhoneForC7 = (phone, countryCode) => {
		if (!phone) return phone;
		let cleanPhone = phone.replace(/[^\d+]/g, '');
		if (cleanPhone.startsWith('+')) return cleanPhone;
		
		if (countryCode === 'US' || countryCode === 'CA') {
			if (cleanPhone.length === 10) return `+1${cleanPhone}`;
			if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) return `+${cleanPhone}`;
		} else if (countryCode === 'AU') {
			if(cleanPhone.startsWith('0')) return `+61${cleanPhone.substring(1)}`;
		} else if (countryCode === 'FR') {
			if(cleanPhone.startsWith('0')) return `+33${cleanPhone.substring(1)}`;
		} else if (countryCode === 'PT') {
			if(cleanPhone.startsWith('0')) return `+351${cleanPhone.substring(1)}`;
			if(cleanPhone.length === 9) return `+351${cleanPhone}`;
		} else if (countryCode === 'GB') {
			if(cleanPhone.startsWith('0')) return `+44${cleanPhone.substring(1)}`;
		}
		
		return cleanPhone; // Fallback
	};

	const parsedCountries = parseOptionsString(countryOptions);
	const defaultCountryObj = parsedCountries.find(c => c.isDefault) || parsedCountries[0];
	const initialCountryCode = defaultCountryObj ? defaultCountryObj.value : 'US';

	// ==========================================
	// STATE & HOOKS
	// ==========================================
	const [fields, setFields] = useState({
		firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', 
		country: initialCountryCode, custom: {}, notes: '',
	});
	const [status, setStatus] = useState('idle');
	const [serverMessage, setServerMessage] = useState('');
	const initializedCustomDefaults = useRef(false);

	const customFieldsList = Array.isArray(customFields) ? customFields : [];

	useEffect(() => {
		if (initializedCustomDefaults.current || customFieldsList.length === 0) return;

		setFields((prev) => {
			const initialCustomState = { ...prev.custom };
			let stateChanged = false;

			customFieldsList.forEach((field) => {
				if (field.type === 'radio') {
					const options = parseOptionsString(field.options);
					const defaultOption = options.find((opt) => opt.isDefault);
					if (defaultOption && !initialCustomState[field.slug]) {
						initialCustomState[field.slug] = defaultOption.value;
						stateChanged = true;
					}
				}
			});
			return stateChanged ? { ...prev, custom: initialCustomState } : prev;
		});
		initializedCustomDefaults.current = true;
	}, [customFieldsList]);

	// ==========================================
	// EVENT HANDLERS
	// ==========================================
	const handleFieldChange = (e) => {
		const { id, value, dataset } = e.target;
		if (dataset.isCustom === 'true') {
			setFields((prev) => ({ ...prev, custom: { ...prev.custom, [id]: value } }));
		} else {
			setFields((prev) => ({ ...prev, [id]: value }));
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setStatus('submitting');
		setServerMessage('');

		const payloadCustom = { ...fields.custom };
		if (Array.isArray(hiddenFields)) {
			hiddenFields.forEach((hf) => {
				if (hf.slug) payloadCustom[hf.slug] = hf.value || '';
			});
		}

		const formattedPhone = formatPhoneForC7(fields.phone, fields.country);

		const payload = { 
			...fields, 
			phone: formattedPhone, 
			custom: payloadCustom, 
			tags: tags 
		};

		fetch('/wp-json/c7-signup/v1/submit', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
			body: JSON.stringify(payload),
		})
		.then(response => {
			if (!response.ok) {
				return response.json().then(errData => { throw new Error(errData.message || __('An unknown error occurred.', 'c7vwp')); });
			}
			return response.json();
		})
		.then(() => setStatus('success'))
		.catch((error) => {
			setStatus('error');
			let messageToDisplay = __('An unknown error occurred.', 'c7vwp');
			if (typeof error === 'string') messageToDisplay = error;
			else if (error && typeof error.message === 'string') messageToDisplay = error.message;
			else if (error && typeof error === 'object') messageToDisplay = JSON.stringify(error);
			setServerMessage(messageToDisplay);
		});
	};

	// ==========================================
	// CONDITIONAL RENDERS
	// ==========================================
	if (status === 'success') {
		const finalMessage = typeof successMessage === 'string' ? successMessage : '';
		return <div className="c7-signup-form__success-message" dangerouslySetInnerHTML={{ __html: finalMessage }} />;
	}

	if (status === 'error') {
		const finalError = typeof errorMessage === 'string' ? errorMessage : '';
		return (
			<div className="c7-signup-form__error-message">
				<div dangerouslySetInnerHTML={{ __html: finalError }} />
				{serverMessage && <p className="c7-signup-form__server-error">{serverMessage}</p>}
				<button onClick={() => setStatus('idle')}>{__('Try Again', 'c7vwp')}</button>
			</div>
		);
	}

	// Calculate Dynamic Validation state once for reuse
	const isStateRequired = makeAddressRequired || !!fields.phone || !!fields.zip;
	const isCountryRequired = makeAddressRequired || !!fields.phone || !!fields.zip;
	
	// Dynamic wording based on Country field visibility
	const requiredHelpText = showCountry
		? __('Country and State are required when providing a phone or zip code.', 'c7vwp')
		: __('State is required when providing a phone or zip code.', 'c7vwp');

	// ==========================================
	// MAIN FORM RENDER
	// ==========================================
	return (
		<form className="c7-signup-form__form" onSubmit={handleSubmit}>
			<div className="c7-signup-form__field-row">
				<div className="c7-signup-form__field-group c7-signup-form__field-group--first-name">
					<label htmlFor="firstName">{firstNameLabel}<span className="c7-signup-form__required">*</span></label>
					<input type="text" id="firstName" value={fields.firstName} onChange={handleFieldChange} required disabled={status === 'submitting'} />
				</div>
				<div className="c7-signup-form__field-group c7-signup-form__field-group--last-name">
					<label htmlFor="lastName">{lastNameLabel}<span className="c7-signup-form__required">*</span></label>
					<input type="text" id="lastName" value={fields.lastName} onChange={handleFieldChange} required disabled={status === 'submitting'} />
				</div>
			</div>
			
			<div className="c7-signup-form__field-row">
				<div className="c7-signup-form__field-group c7-signup-form__field-group--email">
					<label htmlFor="email">{emailLabel}<span className="c7-signup-form__required">*</span></label>
					<input type="email" id="email" value={fields.email} onChange={handleFieldChange} required disabled={status === 'submitting'} />
				</div>
			</div>

			{showAddress && (
				<>
					<div className="c7-signup-form__field-group c7-signup-form__field-group--address1">
						<label htmlFor="address1">{address1Label}{makeAddressRequired && <span className="c7-signup-form__required">*</span>}</label>
						<input type="text" id="address1" value={fields.address1} onChange={handleFieldChange} required={makeAddressRequired} disabled={status === 'submitting'} />
					</div>
					<div className="c7-signup-form__field-group c7-signup-form__field-group--address2">
						<label htmlFor="address2">{address2Label}</label>
						<input type="text" id="address2" value={fields.address2} onChange={handleFieldChange} disabled={status === 'submitting'} />
					</div>
					<div className="c7-signup-form__field-row">
						<div className="c7-signup-form__field-group c7-signup-form__field-group--city">
							<label htmlFor="city">{cityLabel}{makeAddressRequired && <span className="c7-signup-form__required">*</span>}</label>
							<input type="text" id="city" value={fields.city} onChange={handleFieldChange} required={makeAddressRequired} disabled={status === 'submitting'} />
						</div>
						<div className="c7-signup-form__field-group c7-signup-form__field-group--state">
							<label htmlFor="state">{stateLabel}{isStateRequired && <span className="c7-signup-form__required">*</span>}</label>
							<input type="text" id="state" value={fields.state} onChange={handleFieldChange} required={isStateRequired} disabled={status === 'submitting'} placeholder={__('e.g., CA, OR, WA', 'c7vwp')} />
							<small>{__('Please use standard abbreviation.', 'c7vwp')}</small>
						</div>
						<div className="c7-signup-form__field-group c7-signup-form__field-group--zip">
							<label htmlFor="zip">{zipLabel}{makeAddressRequired && <span className="c7-signup-form__required">*</span>}</label>
							<input type="text" id="zip" value={fields.zip} onChange={handleFieldChange} required={makeAddressRequired} disabled={status === 'submitting'} />
						</div>
					</div>

					{showCountry && (
						<div className="c7-signup-form__field-group c7-signup-form__field-group--country">
							<label htmlFor="country">{countryLabel}{isCountryRequired && <span className="c7-signup-form__required">*</span>}</label>
							<select id="country" value={fields.country} onChange={handleFieldChange} required={isCountryRequired} disabled={status === 'submitting'}>
								{parsedCountries.map((c, i) => (
									<option key={i} value={c.value}>{c.label}</option>
								))}
							</select>
						</div>
					)}
					
					{/* Render Dynamic Help Text outside the Country div so it shows even if Country is hidden */}
					{(!!fields.phone || !!fields.zip) && !makeAddressRequired && (
						<div className="c7-signup-form__field-group c7-signup-form__field-group--help-text">
							<small>{requiredHelpText}</small>
						</div>
					)}

					<div className="c7-signup-form__field-group c7-signup-form__field-group--phone">
						<label htmlFor="phone">{phoneLabel}{phoneIsRequired && <span className="c7-signup-form__required">*</span>}</label>
						<input type="tel" id="phone" value={fields.phone} onChange={handleFieldChange} required={phoneIsRequired} disabled={status === 'submitting'} />
					</div>
				</>
			)}

			{customFieldsList.map((field, index) => {
				const modifierClass = field.slug ? `c7-signup-form__field-group--${field.slug}` : '';

				if (field.type === 'message') {
					const messageHtml = typeof field.label === 'string' ? field.label : '';
					return <div key={field.uniqueId || index} className={`c7-signup-form__message ${field.cssClass}`} dangerouslySetInnerHTML={{ __html: messageHtml }} />;
				}

				if (field.type === 'checkbox') {
					return (
						<div key={field.uniqueId || index} className={`c7-signup-form__field-group c7-signup-form__field-group--checkbox ${modifierClass}`}>
							<label>
								<input type="checkbox" id={field.slug} checked={!!fields.custom[field.slug]} onChange={(e) => { const val = e.target.checked; setFields(prev => ({ ...prev, custom: { ...prev.custom, [field.slug]: val } })); }} disabled={status === 'submitting'} required={field.required} />
								{field.label} {field.required && <span className="c7-signup-form__required">*</span>}
							</label>
						</div>
					);
				}

				if (field.type === 'radio') {
					const options = parseOptionsString(field.options);
					return (
						<div key={field.uniqueId || index} className={`c7-signup-form__field-group c7-signup-form__field-group--radio ${modifierClass}`}>
							{field.label && <p className="c7-signup-form__radio-group-label">{field.label} {field.required && <span className="c7-signup-form__required">*</span>}</p>}
							<div className="c7-signup-form__radio-options">
								{options.map((opt, i) => (
									<label key={i}>
										<input type="radio" name={field.slug} id={field.slug} value={opt.value} checked={fields.custom[field.slug] === opt.value} onChange={handleFieldChange} data-is-custom="true" disabled={status === 'submitting'} required={field.required} />
										{opt.label}
									</label>
								))}
							</div>
						</div>
					);
				}

				return (
					<div key={field.uniqueId || index} className={`c7-signup-form__field-group ${modifierClass}`}>
						<label htmlFor={field.slug}>{field.label} {field.required && <span className="c7-signup-form__required">*</span>}</label>
						<input type="text" id={field.slug} value={fields.custom[field.slug] || ''} onChange={handleFieldChange} data-is-custom="true" disabled={status === 'submitting'} required={field.required} />
					</div>
				);
			})}

			{showNotes && (
				<div className="c7-signup-form__field-group c7-signup-form__field-group--notes">
					<label htmlFor="notes">{notesLabel}</label>
					<textarea id="notes" value={fields.notes} onChange={handleFieldChange} disabled={status === 'submitting'} rows="4" />
				</div>
			)}

			<div className="c7-signup-form__submit-group">
				<button type="submit" className="c7-signup-form__submit-button" disabled={status === 'submitting'}>
					{status === 'submitting' ? __('Submitting...', 'c7vwp') : submitButtonLabel}
				</button>
			</div>
		</form>
	);
};

const blockWrappers = document.querySelectorAll('.c7-signup-form');
if (blockWrappers.length) {
	blockWrappers.forEach((wrapper) => {
		const attributes = JSON.parse(wrapper.dataset.attributes);
		render(<FrontendForm attributes={attributes} />, wrapper);
	});
}